import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, DollarSign, ArrowLeft, Filter, Calendar, Download, Smartphone, TrendingUp, Users, BarChart3, Gift, Send } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const AdminDashboardNew = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [pendingFees, setPendingFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('payments'); // 'payments', 'upi', 'pwa', 'coupons'
  const [pwaAnalytics, setPwaAnalytics] = useState(null);
  const [pwaLoading, setPwaLoading] = useState(false);
  
  // Free Coupons State
  const [freeCoupons, setFreeCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponFormData, setCouponFormData] = useState({
    recipient_email: '',
    plan_type: 'basic'
  });
  const [generatingCoupon, setGeneratingCoupon] = useState(false);
  
  // UPI Settings
  const [upiSettings, setUpiSettings] = useState([]);
  const [showAddUpiModal, setShowAddUpiModal] = useState(false);
  const [editingUpi, setEditingUpi] = useState(null);
  const [upiFormData, setUpiFormData] = useState({
    upi_id: '',
    upi_name: '',
    is_active: true
  });
  
  // Filters
  const [filters, setFilters] = useState({
    transaction_date: '',
    transaction_id: '',
    amount: '',
    email: '',
    date_from: '',
    date_to: '',
    dateRange: 'all' // 'all', 'today', 'week', 'month', 'custom'
  });

  const [showAllTransactions, setShowAllTransactions] = useState(true);

  useEffect(() => {
    fetchPendingFees();
  }, [showAllTransactions]);

  useEffect(() => {
    if (activeTab === 'pwa') {
      fetchPWAAnalytics();
    }
    if (activeTab === 'coupons') {
      fetchFreeCoupons();
    }
  }, [activeTab]);

  const fetchFreeCoupons = async () => {
    setCouponsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/free-coupons`, { withCredentials: true });
      setFreeCoupons(response.data.coupons || []);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
      toast.error('Failed to load coupons');
    } finally {
      setCouponsLoading(false);
    }
  };

  const handleGenerateCoupon = async (e) => {
    e.preventDefault();
    if (!couponFormData.recipient_email.trim()) {
      toast.error('Please enter recipient email');
      return;
    }
    
    setGeneratingCoupon(true);
    try {
      const response = await axios.post(`${API}/admin/generate-free-coupon`, couponFormData, { withCredentials: true });
      toast.success(response.data.message || 'Coupon generated and sent!');
      setCouponFormData({ recipient_email: '', plan_type: 'basic' });
      fetchFreeCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate coupon');
    } finally {
      setGeneratingCoupon(false);
    }
  };

  const fetchPendingFees = async (appliedFilters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedFilters.transaction_date) params.append('transaction_date', appliedFilters.transaction_date);
      if (appliedFilters.transaction_id) params.append('transaction_id', appliedFilters.transaction_id);
      if (appliedFilters.amount) params.append('amount', appliedFilters.amount);
      if (appliedFilters.email) params.append('email', appliedFilters.email);
      if (appliedFilters.date_from) params.append('date_from', appliedFilters.date_from);
      if (appliedFilters.date_to) params.append('date_to', appliedFilters.date_to);
      
      // Include verified transactions (for showing all)
      params.append('include_verified', showAllTransactions);

      const response = await axios.get(`${API}/admin/pending-event-fees?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingFees(response.data.fees || []);
    } catch (error) {
      toast.error('Failed to load fees');
    } finally {
      setLoading(false);
    }
  };

  const fetchPWAAnalytics = async () => {
    setPwaLoading(true);
    try {
      const response = await axios.get(`${API}/admin/pwa-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPwaAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch PWA analytics:', error);
      toast.error('Failed to load PWA analytics');
    } finally {
      setPwaLoading(false);
    }
  };

  const fetchUpiSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpiSettings(response.data.settings || []);
    } catch (error) {
      console.error('Failed to fetch UPI settings:', error);
      toast.error('Failed to load UPI settings');
    }
  };

  const handleAddUpiSetting = async () => {
    if (!upiFormData.upi_id || !upiFormData.upi_name) {
      toast.error('UPI ID and name are required');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/payment-settings`,
        upiFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('UPI setting added successfully');
        setShowAddUpiModal(false);
        setUpiFormData({ upi_id: '', upi_name: '', is_active: true });
        fetchUpiSettings();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add UPI setting';
      toast.error(errorMsg);
    }
  };

  const handleUpdateUpiSetting = async () => {
    if (!upiFormData.upi_id || !upiFormData.upi_name) {
      toast.error('UPI ID and name are required');
      return;
    }

    try {
      const response = await axios.put(
        `${API}/admin/payment-settings/${editingUpi.id}`,
        upiFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('UPI setting updated successfully');
        setEditingUpi(null);
        setUpiFormData({ upi_id: '', upi_name: '', is_active: true });
        fetchUpiSettings();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update UPI setting';
      toast.error(errorMsg);
    }
  };

  const handleDeleteUpiSetting = async (settingId) => {
    if (!window.confirm('Are you sure you want to delete this UPI setting?')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API}/admin/payment-settings/${settingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('UPI setting deleted');
        fetchUpiSettings();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete UPI setting';
      toast.error(errorMsg);
    }
  };

  const handleToggleUpiActive = async (settingId, currentStatus) => {
    try {
      const response = await axios.put(
        `${API}/admin/payment-settings/${settingId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`UPI setting ${!currentStatus ? 'activated' : 'deactivated'}`);
        fetchUpiSettings();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update UPI setting';
      toast.error(errorMsg);
    }
  };

  useEffect(() => {
    if (activeTab === 'upi') {
      fetchUpiSettings();
    }
  }, [activeTab]);

  const handleVerify = async (feeId, status) => {
    try {
      await axios.put(
        `${API}/admin/verify-event-fee/${feeId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Payment ${status === 'verified' ? 'approved' : 'rejected'}`);
      fetchPendingFees(filters);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const applyFilters = () => {
    fetchPendingFees(filters);
  };

  const clearFilters = () => {
    setFilters({
      transaction_date: '',
      transaction_id: '',
      amount: '',
      email: '',
      date_from: '',
      date_to: '',
      dateRange: 'all'
    });
    fetchPendingFees({});
  };

  const downloadTracker = () => {
    if (pendingFees.length === 0) {
      toast.error('No transactions to download');
      return;
    }

    const headers = [
      'Transaction Type',
      'Event Creator Email',
      'Plan Type',
      'Original Plan Price (₹)',
      'Coupon Code',
      'Discount (₹)',
      'Final Amount (₹)',
      'Transaction ID',
      'Transaction Date',
      'Payment Status',
      'Verified By',
      'Event Title',
      'Event Date',
      'Created At'
    ];

    const csvRows = [
      headers.join(','),
      ...pendingFees.map(fee => [
        fee.is_upgrade ? 'PLAN UPGRADE' : 'NEW EVENT FEE',
        `"${fee.user_email || 'N/A'}"`,
        fee.plan_type.toUpperCase(),
        fee.original_amount || 0,
        `"${fee.coupon_code || 'None'}"`,
        fee.discount_amount || 0,
        fee.fee_amount || 0,
        `"${fee.transaction_id || 'N/A'}"`,
        fee.transaction_date || 'N/A',
        fee.payment_status.toUpperCase(),
        `"${fee.verified_by || 'Pending'}"`,
        `"${fee.event_title || 'Not Created Yet'}"`,
        fee.event_date || 'N/A',
        fee.created_at ? new Date(fee.created_at).toLocaleDateString() : 'N/A'
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contribo_admin_transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Tracker downloaded successfully');
  };

  const downloadPWAEvents = async () => {
    try {
      toast.info('Fetching PWA events...');
      
      const response = await axios.get(`${API}/admin/pwa-analytics/all-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const events = response.data.events || [];
      
      if (events.length === 0) {
        toast.error('No PWA events to download');
        return;
      }

      // CSV Headers
      const headers = [
        'Event Type',
        'User ID',
        'Device ID',
        'Platform',
        'User Logged In',
        'User Agent',
        'Timestamp',
        'Additional Data'
      ];

      // Convert events to CSV rows
      const csvRows = [
        headers.join(','),
        ...events.map(event => [
          `"${event.event_type || 'N/A'}"`,
          `"${event.user_id || 'Anonymous'}"`,
          `"${event.device_id || 'N/A'}"`,
          `"${event.platform || 'unknown'}"`,
          event.user_logged_in ? 'Yes' : 'No',
          `"${(event.user_agent || 'N/A').substring(0, 100)}"`, // Truncate long user agents
          `"${new Date(event.timestamp).toLocaleString()}"`,
          `"${JSON.stringify(event.additional_data || {}).replace(/"/g, "'")}"`
        ].join(','))
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pwa_events_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${events.length} PWA events`);
    } catch (error) {
      console.error('Failed to download PWA events:', error);
      toast.error('Failed to download PWA events');
    }
  };

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let date_from = '';
    let date_to = today.toISOString().split('T')[0];

    if (range === 'today') {
      date_from = date_to;
    } else if (range === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      date_from = weekAgo.toISOString().split('T')[0];
    } else if (range === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      date_from = monthAgo.toISOString().split('T')[0];
    }

    setFilters({ ...filters, dateRange: range, date_from, date_to: range === 'all' ? '' : date_to });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <Button variant="ghost" onClick={() => navigate('/')} className="mb-2">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === 'payments'
                  ? 'text-contribo-navy border-b-2 border-contribo-navy'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <DollarSign className="inline h-4 w-4 mr-2" />
              Event Fee Approvals
            </button>
            <button
              onClick={() => setActiveTab('upi')}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === 'upi'
                  ? 'text-contribo-navy border-b-2 border-contribo-navy'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <DollarSign className="inline h-4 w-4 mr-2" />
              UPI Settings
            </button>
            <button
              onClick={() => setActiveTab('pwa')}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === 'pwa'
                  ? 'text-contribo-navy border-b-2 border-contribo-navy'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Smartphone className="inline h-4 w-4 mr-2" />
              PWA Analytics
            </button>
            <button
              onClick={() => setActiveTab('coupons')}
              className={`px-4 py-2 font-semibold transition-colors ${
                activeTab === 'coupons'
                  ? 'text-contribo-navy border-b-2 border-contribo-navy'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Gift className="inline h-4 w-4 mr-2" />
              Free Coupons
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'payments' && (
          <>
        {/* Metrics Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700 font-semibold mb-1">Total Events</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {pendingFees.length}
                  </p>
                </div>
                <div className="bg-blue-200 p-3 rounded-full">
                  <Calendar className="h-6 w-6 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700 font-semibold mb-1">Total Approved</p>
                  <p className="text-3xl font-bold text-green-900">
                    {pendingFees.filter(f => f.payment_status === 'verified').length}
                  </p>
                </div>
                <div className="bg-green-200 p-3 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700 font-semibold mb-1">Pending Approval</p>
                  <p className="text-3xl font-bold text-yellow-900">
                    {pendingFees.filter(f => f.payment_status === 'pending').length}
                  </p>
                </div>
                <div className="bg-yellow-200 p-3 rounded-full">
                  <XCircle className="h-6 w-6 text-yellow-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-700 font-semibold mb-1">Pending Amount</p>
                  <p className="text-2xl font-bold text-purple-900">
                    ₹{pendingFees.filter(f => f.payment_status === 'pending').reduce((sum, f) => sum + f.fee_amount, 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-purple-200 p-3 rounded-full">
                  <DollarSign className="h-6 w-6 text-purple-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div>
                <Label>Transaction ID</Label>
                <Input
                  placeholder="Search by txn ID"
                  value={filters.transaction_id}
                  onChange={(e) => setFilters({...filters, transaction_id: e.target.value})}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  placeholder="Search by email"
                  value={filters.email}
                  onChange={(e) => setFilters({...filters, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Amount</Label>
                <Input
                  type="number"
                  placeholder="Filter by amount"
                  value={filters.amount}
                  onChange={(e) => setFilters({...filters, amount: e.target.value})}
                />
              </div>
              <div>
                <Label>Date Range</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={filters.dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
            </div>

            {filters.dateRange === 'custom' && (
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  />
                </div>
                <div>
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={applyFilters} className="bg-contribo-navy">
                Apply Filters
              </Button>
              <Button onClick={clearFilters} variant="outline">
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* All Transactions */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-6 w-6 text-contribo-navy" />
                {showAllTransactions ? 'All Transactions' : 'Pending Transactions'} ({pendingFees.length})
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowAllTransactions(!showAllTransactions)}
                  variant="outline"
                  size="sm"
                >
                  {showAllTransactions ? 'Show Pending Only' : 'Show All'}
                </Button>
                <Button 
                  onClick={downloadTracker}
                  className="bg-green-600 hover:bg-green-700"
                  size="sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Tracker
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Loading...</p>
            ) : pendingFees.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No transactions found</p>
            ) : (
              <div className="space-y-4">
                {pendingFees.map((fee) => (
                  <div key={fee.id} className={`border-2 rounded-lg p-4 hover:shadow-md transition-shadow ${
                    fee.payment_status === 'verified' ? 'bg-green-50 border-green-300' : 
                    fee.payment_status === 'rejected' ? 'bg-red-50 border-red-300' : 
                    'border-yellow-300'
                  }`}>
                    {/* Status Badge */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          fee.payment_status === 'verified' ? 'bg-green-200 text-green-800' :
                          fee.payment_status === 'rejected' ? 'bg-red-200 text-red-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {fee.payment_status.toUpperCase()}
                        </span>
                        {fee.is_upgrade && (
                          <span className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-xs font-bold">
                            🔄 PLAN UPGRADE
                          </span>
                        )}
                      </div>
                      {fee.coupon_code && (
                        <span className="px-3 py-1 bg-purple-200 text-purple-800 rounded-full text-xs font-bold">
                          🎟️ {fee.coupon_code}
                        </span>
                      )}
                    </div>

                    {/* Main Details Grid */}
                    <div className="grid md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-600">Creator Email</p>
                        <p className="font-semibold text-sm">{fee.user_email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Plan Type</p>
                        <p className="font-semibold text-sm capitalize">{fee.plan_type} Plan</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Transaction Date</p>
                        <p className="font-semibold text-sm">{fee.transaction_date || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Pricing Details */}
                    <div className="bg-white rounded-lg p-3 mb-3 border">
                      <p className="text-xs font-semibold text-gray-700 mb-2">💰 Pricing Details</p>
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <p className="text-xs text-gray-600">Original Price</p>
                          <p className="font-bold text-gray-900">₹{fee.original_amount || fee.fee_amount}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Discount</p>
                          <p className="font-bold text-red-600">-₹{fee.discount_amount || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Final Amount</p>
                          <p className="font-bold text-green-600">₹{fee.fee_amount}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Budget</p>
                          <p className="font-semibold">₹{fee.total_budget?.toLocaleString() || 'Pending'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Transaction ID */}
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                      <p className="text-xs text-gray-600 mb-1">Transaction ID</p>
                      <p className="font-mono text-sm font-bold text-blue-900">{fee.transaction_id || 'N/A'}</p>
                    </div>

                    {/* Event Details */}
                    {fee.event_title && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-600">Event Created</p>
                        <p className="font-semibold text-sm">{fee.event_title} ({fee.event_date})</p>
                      </div>
                    )}

                    {/* Verification Info */}
                    {fee.verified_by && (
                      <div className="text-xs text-gray-600 mb-3">
                        Verified by: <strong>{fee.verified_by}</strong> at {fee.verified_at ? new Date(fee.verified_at).toLocaleString() : 'N/A'}
                      </div>
                    )}

                    {/* Action Buttons - Only for Pending */}
                    {fee.payment_status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleVerify(fee.id, 'verified')}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleVerify(fee.id, 'rejected')}
                          variant="destructive"
                          className="flex-1"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>
        )}

        {activeTab === 'pwa' && (
          <>
            {/* PWA Analytics Section */}
            <div className="mb-8">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">PWA Installation Analytics</h2>
                  <p className="text-gray-600 mt-2">
                    Track Progressive Web App (PWA) installations and user engagement metrics powered by Google Analytics 4.
                  </p>
                </div>
                <Button
                  onClick={downloadPWAEvents}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={pwaLoading || !pwaAnalytics?.recent_events?.length}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Events
                </Button>
              </div>

              {/* PWA Metrics */}
              {pwaLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-contribo-navy mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading PWA analytics...</p>
                </div>
              ) : (
                <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-700 font-semibold mb-1">PWA Installs</p>
                        <p className="text-3xl font-bold text-blue-900">
                          {pwaAnalytics?.total_installs || 0}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">Total installations</p>
                      </div>
                      <div className="bg-blue-200 p-3 rounded-full">
                        <Smartphone className="h-6 w-6 text-blue-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-700 font-semibold mb-1">PWA Launches</p>
                        <p className="text-3xl font-bold text-green-900">
                          {pwaAnalytics?.total_launches || 0}
                        </p>
                        <p className="text-xs text-green-600 mt-1">App opened from home screen</p>
                      </div>
                      <div className="bg-green-200 p-3 rounded-full">
                        <TrendingUp className="h-6 w-6 text-green-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-700 font-semibold mb-1">Button Clicks</p>
                        <p className="text-3xl font-bold text-purple-900">
                          {pwaAnalytics?.install_button_clicks || 0}
                        </p>
                        <p className="text-xs text-purple-600 mt-1">Install button clicks</p>
                      </div>
                      <div className="bg-purple-200 p-3 rounded-full">
                        <Users className="h-6 w-6 text-purple-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-700 font-semibold mb-1">Conversion Rate</p>
                        <p className="text-3xl font-bold text-orange-900">
                          {pwaAnalytics?.conversion_rate || 0}%
                        </p>
                        <p className="text-xs text-orange-600 mt-1">Click to install rate</p>
                      </div>
                      <div className="bg-orange-200 p-3 rounded-full">
                        <BarChart3 className="h-6 w-6 text-orange-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Platform Breakdown */}
              {pwaAnalytics?.platform_breakdown && Object.keys(pwaAnalytics.platform_breakdown).length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>Platform Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(pwaAnalytics.platform_breakdown).map(([platform, count]) => (
                        <div key={platform} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <Smartphone className="h-5 w-5 text-gray-600" />
                            <span className="font-semibold capitalize">{platform}</span>
                          </div>
                          <span className="text-2xl font-bold text-contribo-navy">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              </>
              )}

              {/* GA4 Integration Guide */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-6 w-6 text-contribo-navy" />
                    How to View PWA Analytics in Google Analytics 4
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                      <h4 className="font-semibold text-blue-900 mb-2">📊 Your GA4 Measurement ID</h4>
                      <p className="font-mono text-blue-700 bg-white px-3 py-2 rounded border">G-QHZ0WNF43T</p>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-900">Step-by-step Guide:</h4>
                      
                      <div className="space-y-2">
                        <div className="flex gap-3 items-start">
                          <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                          <div>
                            <p className="font-semibold text-gray-800">Access Google Analytics 4</p>
                            <p className="text-sm text-gray-600">Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">analytics.google.com</a> and select your property (G-QHZ0WNF43T)</p>
                          </div>
                        </div>

                        <div className="flex gap-3 items-start">
                          <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                          <div>
                            <p className="font-semibold text-gray-800">View PWA Install Events</p>
                            <p className="text-sm text-gray-600">Navigate to <strong>Reports → Engagement → Events</strong></p>
                            <p className="text-sm text-gray-600">Look for the custom event: <code className="bg-gray-100 px-2 py-1 rounded text-xs">pwa_installed</code></p>
                          </div>
                        </div>

                        <div className="flex gap-3 items-start">
                          <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                          <div>
                            <p className="font-semibold text-gray-800">View PWA Launch Events</p>
                            <p className="text-sm text-gray-600">In the same Events section, look for: <code className="bg-gray-100 px-2 py-1 rounded text-xs">pwa_launch</code></p>
                            <p className="text-sm text-gray-600">This tracks when users open the app from their home screen</p>
                          </div>
                        </div>

                        <div className="flex gap-3 items-start">
                          <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                          <div>
                            <p className="font-semibold text-gray-800">Create Custom Reports</p>
                            <p className="text-sm text-gray-600">Go to <strong>Explore</strong> section → Create a new exploration</p>
                            <p className="text-sm text-gray-600">Add metrics: Event count, Event name</p>
                            <p className="text-sm text-gray-600">Filter by: <code className="bg-gray-100 px-2 py-1 rounded text-xs">pwa_installed</code> or <code className="bg-gray-100 px-2 py-1 rounded text-xs">pwa_launch</code></p>
                          </div>
                        </div>

                        <div className="flex gap-3 items-start">
                          <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                          <div>
                            <p className="font-semibold text-gray-800">Platform Breakdown</p>
                            <p className="text-sm text-gray-600">In custom reports, add dimension: <strong>Platform</strong> (tracked in event parameters)</p>
                            <p className="text-sm text-gray-600">This shows iOS vs Android installation rates</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mt-6">
                      <h4 className="font-semibold text-green-900 mb-2">✅ Events Being Tracked</h4>
                      <ul className="space-y-1 text-sm text-green-800">
                        <li>• <strong>pwa_installed:</strong> When users add the app to their home screen</li>
                        <li>• <strong>pwa_launch:</strong> When users open the app from their home screen</li>
                        <li>• <strong>login:</strong> User authentication events</li>
                        <li>• <strong>page_view:</strong> All page navigation tracking</li>
                      </ul>
                    </div>

                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded mt-4">
                      <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Note on Data Availability</h4>
                      <p className="text-sm text-yellow-800">
                        GA4 data typically appears within 24-48 hours. If you just enabled tracking, 
                        you may see minimal data initially. Data will accumulate as users interact with your app.
                      </p>
                    </div>

                    <div className="mt-4 flex gap-3">
                      <Button
                        onClick={() => window.open('https://analytics.google.com', '_blank')}
                        className="bg-contribo-navy hover:bg-contribo-gold"
                      >
                        Open Google Analytics
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => window.open('https://support.google.com/analytics/answer/9267735', '_blank')}
                      >
                        GA4 Documentation
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional PWA Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-6 w-6 text-contribo-navy" />
                    PWA Features Enabled
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900">Installable PWA</p>
                        <p className="text-sm text-green-700">Users can install the app on their device</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900">Offline Support</p>
                        <p className="text-sm text-green-700">Service worker enabled for caching</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900">Analytics Tracking</p>
                        <p className="text-sm text-green-700">Install and launch events tracked</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900">Cross-Platform</p>
                        <p className="text-sm text-green-700">Works on iOS, Android, and Desktop</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* UPI Settings Tab */}
        {activeTab === 'upi' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center justify-between">
                  <span>UPI Payment Settings</span>
                  <Button
                    onClick={() => {
                      setShowAddUpiModal(true);
                      setUpiFormData({ upi_id: '', upi_name: '', is_active: true });
                    }}
                    className="bg-contribo-navy hover:bg-contribo-gold"
                  >
                    Add UPI ID
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upiSettings.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <DollarSign className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                    <p className="text-lg font-semibold">No UPI settings configured</p>
                    <p className="text-sm mt-2">Add a UPI ID to receive payments from hosts</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upiSettings.map((setting) => (
                      <div
                        key={setting.id}
                        className={`p-4 border-2 rounded-lg ${
                          setting.is_active
                            ? 'border-green-400 bg-green-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-gray-800">{setting.upi_name}</h3>
                              {setting.is_active && (
                                <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                                  ACTIVE
                                </span>
                              )}
                            </div>
                            <p className="text-gray-700 font-mono text-sm mb-2">{setting.upi_id}</p>
                            <p className="text-xs text-gray-500">
                              Created: {new Date(setting.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => {
                                setEditingUpi(setting);
                                setUpiFormData({
                                  upi_id: setting.upi_id,
                                  upi_name: setting.upi_name,
                                  is_active: setting.is_active
                                });
                              }}
                              size="sm"
                              variant="outline"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleToggleUpiActive(setting.id, setting.is_active)}
                              size="sm"
                              variant="outline"
                              className={setting.is_active ? 'text-orange-600' : 'text-green-600'}
                            >
                              {setting.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              onClick={() => handleDeleteUpiSetting(setting.id)}
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>How It Works</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex items-start gap-3">
                    <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                    <p><strong>Add UPI ID:</strong> Configure the UPI ID where hosts should make payments</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                    <p><strong>Set Active:</strong> Only one UPI ID can be active at a time. This is the one shown to hosts</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                    <p><strong>Masked Display:</strong> Hosts see a masked version of the UPI ID for security (e.g., anu***@okicici)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-contribo-navy text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                    <p><strong>Auto-Update:</strong> When you change the active UPI ID, it automatically updates for all future payments</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add/Edit UPI Modal */}
        {(showAddUpiModal || editingUpi) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {editingUpi ? 'Edit UPI Setting' : 'Add UPI Setting'}
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label>UPI ID *</Label>
                    <Input
                      value={upiFormData.upi_id}
                      onChange={(e) => setUpiFormData({ ...upiFormData, upi_id: e.target.value })}
                      placeholder="e.g., anushaammu1103@okicici"
                    />
                  </div>
                  <div>
                    <Label>Display Name *</Label>
                    <Input
                      value={upiFormData.upi_name}
                      onChange={(e) => setUpiFormData({ ...upiFormData, upi_name: e.target.value })}
                      placeholder="e.g., Contribo Admin"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={upiFormData.is_active}
                      onChange={(e) => setUpiFormData({ ...upiFormData, is_active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label>Set as Active</Label>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => {
                      setShowAddUpiModal(false);
                      setEditingUpi(null);
                      setUpiFormData({ upi_id: '', upi_name: '', is_active: true });
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={editingUpi ? handleUpdateUpiSetting : handleAddUpiSetting}
                    className="flex-1 bg-contribo-navy hover:bg-contribo-gold"
                  >
                    {editingUpi ? 'Update' : 'Add'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Free Coupons Tab */}
        {activeTab === 'coupons' && (
          <div className="space-y-6">
            {/* Generate New Coupon */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-purple-600" />
                  Generate Free Plan Coupon
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGenerateCoupon} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Recipient Email *</Label>
                      <Input
                        type="email"
                        value={couponFormData.recipient_email}
                        onChange={(e) => setCouponFormData({ ...couponFormData, recipient_email: e.target.value })}
                        placeholder="user@example.com"
                        required
                      />
                    </div>
                    <div>
                      <Label>Plan Type *</Label>
                      <select
                        value={couponFormData.plan_type}
                        onChange={(e) => setCouponFormData({ ...couponFormData, plan_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="basic">Basic (₹49 - Budget: ₹20,000)</option>
                        <option value="standard">Standard (₹99 - Budget: ₹50,000)</option>
                        <option value="premium">Premium (₹149 - Budget: ₹2,00,000)</option>
                        <option value="enterprise">Elite (₹199 - Budget: ₹10,00,000)</option>
                      </select>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={generatingCoupon}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {generatingCoupon ? (
                      <>Generating...</>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Generate & Send Coupon
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Generated Coupons List */}
            <Card>
              <CardHeader>
                <CardTitle>Generated Free Coupons</CardTitle>
              </CardHeader>
              <CardContent>
                {couponsLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading coupons...</div>
                ) : freeCoupons.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No free coupons generated yet</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3">Code</th>
                          <th className="text-left p-3">Recipient</th>
                          <th className="text-left p-3">Plan</th>
                          <th className="text-left p-3">Status</th>
                          <th className="text-left p-3">Created</th>
                          <th className="text-left p-3">Expires</th>
                        </tr>
                      </thead>
                      <tbody>
                        {freeCoupons.map((coupon) => (
                          <tr key={coupon.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-mono font-bold text-purple-700">{coupon.code}</td>
                            <td className="p-3">{coupon.recipient_email}</td>
                            <td className="p-3 capitalize">{coupon.plan_type}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                coupon.status === 'active' ? 'bg-green-100 text-green-700' :
                                coupon.status === 'used' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {coupon.status}
                              </span>
                            </td>
                            <td className="p-3 text-gray-500">
                              {new Date(coupon.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-3 text-gray-500">
                              {new Date(coupon.expires_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboardNew;
